import React, { useCallback, useState } from "react";
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";

import {
  ChannelPreference,
  CommandResponse,
  DeviceCommand,
  FieldDevice,
  FieldDeviceConfigurationComponent,
  FieldDeviceReportedConfig,
  SoftwarePackageType
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  useDispatchDeviceCommand,
  useSoftwarePackageCommandJson
} from "@libs/api/gateways/field/field-ops-gateway.hooks";

import { SendCommandDialog } from "./SendCommandDialog";
import { SoftwarePackageCommandsBody } from "./SoftwarePackageCommandsBody";
import {
  SelectedVersionState,
  SoftwarePackageHeader
} from "./SoftwarePackageHeader";

interface SoftwarePackageCommandsProps {
  reportedConfig: FieldDeviceReportedConfig | undefined;
  desiredConfig: FieldDeviceConfigurationComponent | undefined;
  device?: FieldDevice;
}

export const MESSAGE_TYPE_TEMPLATE =
  "Bps.Platform.FieldManagement.OnSite.FieldController.Models.{type}, Bps.Platform.FieldManagement.OnSite.FieldController, Version=0.0.0.0, Culture=neutral, PublicKeyToken=null";

export const SoftwarePackageCommands: React.FC<SoftwarePackageCommandsProps> = ({
  desiredConfig,
  reportedConfig,
  device
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { softwarePackageCode } = useParams();

  // State
  const [dispatchFormShown, setDispatchFormShown] = useState(false);
  const [formData, setFormData] = useState<DeviceCommand>();
  const [selectedVersion, setSelectedVersion] = useState<SelectedVersionState>(
    desiredConfig !== undefined
      ? SelectedVersionState.DesiredConfig
      : SelectedVersionState.ReportedConfig
  );

  // Queries
  const { data: commandJson } = useSoftwarePackageCommandJson(
    selectedVersion === SelectedVersionState.ReportedConfig
      ? reportedConfig?.softwarePackageVersionId
      : desiredConfig?.id
  );

  const { mutateAsync: deviceCommandDispatch } = useDispatchDeviceCommand();

  const packageType =
    desiredConfig?.type === SoftwarePackageType.Service
      ? "services"
      : "modules";

  const match = matchPath(
    `/field-mgmt/:fieldDeviceId/${packageType}/:softwarePackageCode/:command`,
    location.pathname
  );

  const commandArg = match?.params?.command;
  const getMessageTypeString = () => {
    const command = commandJson?.find(command => command.title === commandArg);

    return (
      command?.["x-messageType"] ??
      MESSAGE_TYPE_TEMPLATE.replace("{type}", commandArg!)
    );
  };

  const moduleCommandKey = `${softwarePackageCode}_${commandArg}`;

  const [dispatchErrors, setDispatchErrors] = useState(
    new Map<string, CommandResponse | undefined>()
  );

  const onCommandChange = useCallback(
    (command: string) => {
      navigate(
        `/field-mgmt/${device?.id}/${packageType}/${softwarePackageCode}/${command}`
      );
    },
    [navigate, device?.id, packageType, softwarePackageCode]
  );

  const onSubmit = async ({ formData }: any) => {
    // We explicitly have any type as form data type changes with command type.
    const command = {
      fieldDeviceId: device?.id!,
      channelPreference: ChannelPreference.RealtimeWithQueuedFallback, // Default to RealtimeWithQueuedFallback
      targetSoftwarePackageCode: softwarePackageCode!,
      routedMessageData: {
        messageType: getMessageTypeString(),
        commandName: commandArg,
        value: JSON.stringify({ ...formData })
      }
    };

    setFormData(command);
    setDispatchFormShown(true);
  };

  const onDispatch = useCallback(async () => {
    try {
      const response = await deviceCommandDispatch(formData!);
      setDispatchErrors(
        new Map(dispatchErrors.set(moduleCommandKey, response))
      );
    } catch (error) {
      setDispatchErrors(
        new Map(
          dispatchErrors.set(moduleCommandKey, {
            ErrorMessage: `${error.message}: ${error.detail}`
          })
        )
      );
    } finally {
      setDispatchFormShown(false);
    }
  }, [
    deviceCommandDispatch,
    formData,
    dispatchErrors,
    setDispatchErrors,
    moduleCommandKey
  ]);

  return (
    <>
      <SoftwarePackageHeader
        fieldDeviceId={device?.id!}
        desiredConfig={desiredConfig}
        reportedConfig={reportedConfig}
        selectedVersion={selectedVersion}
        setSelectedVersion={setSelectedVersion}
      />

      <SoftwarePackageCommandsBody
        commandJson={commandJson}
        commandArg={commandArg}
        handleSubmit={onSubmit}
        handleCommandChange={onCommandChange}
        response={dispatchErrors.get(moduleCommandKey)}
      />

      <SendCommandDialog
        isShown={dispatchFormShown}
        formData={formData}
        onDismiss={() => {
          setDispatchFormShown(false);
        }}
        onDispatch={onDispatch}
        setFormData={setFormData}
      />
    </>
  );
};
